/**
 * Call recording, the client's side of the backend (docs/call-recording.md).
 *
 * The callables in functions/src/callRecording, the recording document, and
 * the upload queue: every chunk a recorder makes is put in IndexedDB first
 * and uploaded from there, one at a time, so a slow connection only delays
 * the file and a closed tab resumes on the next load. When a recording's
 * last chunk is up after this side has left, the participant document says
 * so (uploadsDone), which is what starts the compile. Heartbeats run here,
 * at module level, so they keep going while uploads finish after the call
 * screen has closed.
 */
import { httpsCallable } from 'firebase/functions';
import { doc, onSnapshot, serverTimestamp, updateDoc } from 'firebase/firestore';
import { ref as storageRef, uploadBytes } from 'firebase/storage';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db, functions, storage } from '../firebase';
import { HEARTBEAT_MS, corsReadable, type ClockSample } from '../utils/callRecordingPlan';

// ── Callables ────────────────────────────────────────────────────────────────

export interface RecordingJoin {
  recordingId: string | null;
  enabled: boolean;
  role: 'coach' | 'player' | 'guest' | null;
  serverNowMs: number;
}

export async function joinRecording(roomId: string): Promise<RecordingJoin> {
  const fn = httpsCallable<{ roomId: string }, Partial<RecordingJoin>>(functions, 'joinCallRecording');
  const r = (await fn({ roomId })).data;
  return {
    recordingId: r.recordingId ?? null,
    enabled: r.enabled !== false,
    role: r.role ?? null,
    serverNowMs: Number(r.serverNowMs),
  };
}

/** Three readings of the server's clock, for clockOffset. */
export async function sampleServerClock(n = 3): Promise<ClockSample[]> {
  const fn = httpsCallable<unknown, { serverNowMs: number }>(functions, 'callRecordingClock');
  const out: ClockSample[] = [];
  for (let i = 0; i < n; i++) {
    const sentMs = Date.now();
    try {
      const r = await fn({});
      out.push({ sentMs, receivedMs: Date.now(), serverMs: Number(r.data.serverNowMs) });
    } catch { /* a missed sample; the others still place the clock */ }
  }
  return out;
}

export async function setRecordingEnabled(recordingId: string, enabled: boolean): Promise<void> {
  const fn = httpsCallable<{ recordingId: string; enabled: boolean }, unknown>(functions, 'setCallRecordingEnabled');
  await fn({ recordingId, enabled });
}

export interface RecordingDoc {
  status: 'recording' | 'compiling' | 'ready' | 'failed' | string;
  enabled: boolean;
  kind: 'lesson' | 'call' | string;
}

export function subscribeRecording(recordingId: string, cb: (r: RecordingDoc | null) => void): () => void {
  return onSnapshot(doc(db, 'callRecordings', recordingId), (s) => {
    const d = s.data();
    cb(d ? { status: String(d.status), enabled: d.enabled !== false, kind: String(d.kind) } : null);
  }, () => cb(null));
}

// ── Presence: heartbeat, leave, uploads done ─────────────────────────────────

const participantDoc = (recordingId: string, uid: string) =>
  doc(db, 'callRecordings', recordingId, 'participants', uid);

interface Open { uid: string; left: boolean; done: boolean }
/** Recordings this tab is in, or has left with uploads still to finish. */
const open = new Map<string, Open>();
let beat: ReturnType<typeof setInterval> | null = null;

function ensureBeat() {
  if (beat || open.size === 0) return;
  beat = setInterval(() => {
    for (const [rid, o] of open) {
      if (o.done) continue;
      updateDoc(participantDoc(rid, o.uid), { lastSeenAt: serverTimestamp() }).catch(() => { /* next beat */ });
    }
  }, HEARTBEAT_MS);
}

function stopBeatIfIdle() {
  for (const o of open.values()) if (!o.done) return;
  if (beat) clearInterval(beat);
  beat = null;
}

/** This side is in the recording: beat until it has left and its uploads are up. */
export function enterRecording(recordingId: string, uid: string): void {
  const o = open.get(recordingId);
  if (o && !o.done) { o.left = false; return; }
  open.set(recordingId, { uid, left: false, done: false });
  ensureBeat();
}

/**
 * This side has left. leftAt is written now and never again (the rule
 * allows it once); uploadsDone follows when the queue holds nothing more
 * for this recording.
 */
export function leaveRecording(recordingId: string): void {
  const o = open.get(recordingId);
  if (!o || o.left) return;
  o.left = true;
  void persistLeft(recordingId, o.uid);
  updateDoc(participantDoc(recordingId, o.uid), { lastSeenAt: serverTimestamp(), leftAt: serverTimestamp() })
    .catch(() => { /* the sweep places it by its heartbeat */ })
    .finally(() => { void maybeDone(recordingId); });
}

async function maybeDone(recordingId: string): Promise<void> {
  const o = open.get(recordingId);
  if (!o || !o.left || o.done) return;
  if (await pendingFor(recordingId) > 0) return;
  try {
    await updateDoc(participantDoc(recordingId, o.uid), { lastSeenAt: serverTimestamp(), uploadsDone: true });
    o.done = true;
    void forgetLeft(recordingId);
    stopBeatIfIdle();
  } catch { /* tried again after the next upload or on the next load */ }
}

// ── The upload queue ─────────────────────────────────────────────────────────

interface QueueItem {
  id?: number;
  recordingId: string;
  uid: string;
  path: string;
  contentType: string;
  blob: Blob;
  /** A manifest is rewritten in place; a chunk is written once. */
  replace: boolean;
}

const DB_NAME = 'db-call-recording';
const Q = 'queue';
const LEFT = 'left';
let idb: Promise<IDBDatabase | null> | null = null;
/** Where IndexedDB cannot be opened (a private window), the queue lives here for this tab. */
const memQueue: QueueItem[] = [];
let memId = 1;

function openDb(): Promise<IDBDatabase | null> {
  if (idb) return idb;
  idb = new Promise((resolve) => {
    try {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        const d = req.result;
        d.createObjectStore(Q, { keyPath: 'id', autoIncrement: true }).createIndex('recordingId', 'recordingId');
        d.createObjectStore(LEFT, { keyPath: 'recordingId' });
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return idb;
}

function tx<T>(store: string, mode: IDBTransactionMode, run: (s: IDBObjectStore) => IDBRequest<T>): Promise<T | null> {
  return openDb().then((d) => d ? new Promise<T | null>((resolve) => {
    try {
      const r = run(d.transaction(store, mode).objectStore(store));
      r.onsuccess = () => resolve(r.result);
      r.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  }) : null);
}

async function pendingFor(recordingId: string): Promise<number> {
  const d = await openDb();
  if (!d) return memQueue.filter((x) => x.recordingId === recordingId).length;
  const n = await tx(Q, 'readonly', (s) => s.index('recordingId').count(recordingId));
  return n ?? 0;
}

async function persistLeft(recordingId: string, uid: string) {
  await tx(LEFT, 'readwrite', (s) => s.put({ recordingId, uid }));
}
async function forgetLeft(recordingId: string) {
  await tx(LEFT, 'readwrite', (s) => s.delete(recordingId));
}

let pumping = false;
let pumpAgain = false;
let lastError: unknown = null;
const listeners = new Set<(pending: number) => void>();

/** Adds a file to the queue and starts the queue if it is idle. */
export async function enqueueUpload(item: Omit<QueueItem, 'id'>): Promise<void> {
  const d = await openDb();
  if (d) {
    const saved = await tx(Q, 'readwrite', (s) => s.add(item));
    if (saved === null) memQueue.push({ ...item, id: memId++ });
  } else {
    memQueue.push({ ...item, id: memId++ });
  }
  void pump();
}

async function nextItem(uid: string): Promise<QueueItem | null> {
  const mem = memQueue.find((x) => x.uid === uid);
  if (mem) return mem;
  const d = await openDb();
  if (!d) return null;
  return new Promise((resolve) => {
    try {
      const req = d.transaction(Q, 'readonly').objectStore(Q).openCursor();
      req.onsuccess = () => {
        const c = req.result;
        if (!c) return resolve(null);
        const v = c.value as QueueItem;
        if (v.uid === uid) return resolve(v);
        c.continue();
      };
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function removeItem(item: QueueItem): Promise<void> {
  const i = memQueue.indexOf(item);
  if (i >= 0) { memQueue.splice(i, 1); return; }
  if (item.id !== undefined) await tx(Q, 'readwrite', (s) => s.delete(item.id!));
}

async function totalPending(): Promise<number> {
  const n = await tx(Q, 'readonly', (s) => s.count());
  return (n ?? 0) + memQueue.length;
}

async function notify() {
  const n = await totalPending();
  for (const l of listeners) l(n);
}

/** Uploads the queue in order until it is empty, backing off while offline or refused. */
async function pump(): Promise<void> {
  if (pumping) { pumpAgain = true; return; }
  pumping = true;
  let backoff = 2000;
  try {
    for (;;) {
      const uid = auth.currentUser?.uid;
      if (!uid) break;
      const item = await nextItem(uid);
      if (!item) {
        if (pumpAgain) { pumpAgain = false; continue; }
        break;
      }
      try {
        await uploadBytes(storageRef(storage, item.path), item.blob, { contentType: item.contentType });
        await removeItem(item);
        backoff = 2000;
        lastError = null;
        void notify();
        void maybeDone(item.recordingId);
      } catch (err) {
        const code = (err as { code?: string }).code;
        // A chunk already up (the answer to the first try was lost) cannot be
        // written twice; it is there, so it leaves the queue.
        if (code === 'storage/unauthorized' && !item.replace) {
          await removeItem(item);
          void maybeDone(item.recordingId);
          continue;
        }
        lastError = err;
        await new Promise((r) => setTimeout(r, backoff));
        backoff = Math.min(backoff * 2, 60_000);
      }
    }
  } finally {
    pumping = false;
    void notify();
  }
}

/** The number of files still to upload, now and as it changes. */
export function subscribePending(cb: (pending: number) => void): () => void {
  listeners.add(cb);
  void totalPending().then(cb);
  return () => { listeners.delete(cb); };
}

export function lastUploadError(): unknown {
  return lastError;
}

// ── On load: resume what an earlier tab left ─────────────────────────────────

let resumed = false;
function resume() {
  if (resumed || typeof window === 'undefined') return;
  resumed = true;
  onAuthStateChanged(auth, (user) => {
    if (!user) return;
    void (async () => {
      const lefts = await tx(LEFT, 'readonly', (s) => s.getAll()) as Array<{ recordingId: string; uid: string }> | null;
      for (const l of lefts ?? []) {
        if (l.uid !== user.uid || open.has(l.recordingId)) continue;
        open.set(l.recordingId, { uid: l.uid, left: true, done: false });
      }
      ensureBeat();
      void pump();
      for (const rid of open.keys()) void maybeDone(rid);
    })();
  });
  window.addEventListener('online', () => { void pump(); });
  // Closing the tab with chunks still to upload loses nothing (they resume
  // on the next load), but the file is late; the browser's own prompt asks.
  window.addEventListener('beforeunload', (e) => {
    if (memQueue.length > 0 || [...open.values()].some((o) => !o.done && o.left)) {
      e.preventDefault();
    }
  });
}
resume();

// ── Whether a match video can be drawn for the recording ─────────────────────

const corsChecked = new Map<string, boolean>();
const corsChecking = new Map<string, Promise<boolean>>();

/**
 * Whether this url answers with CORS headers, asked with a one-byte range
 * request. Only then does the player's match load with crossOrigin (a
 * source without them would not load at all), so the recording can draw it.
 */
export function probeCors(url: string): Promise<boolean> {
  const known = corsChecked.get(url);
  if (known !== undefined) return Promise.resolve(known);
  const going = corsChecking.get(url);
  if (going) return going;
  const p = (async () => {
    if (!corsReadable(url, window.location.origin)) return false;
    if (url.startsWith('blob:') || url.startsWith('data:')) return true;
    try {
      const r = await fetch(url, { mode: 'cors', headers: { Range: 'bytes=0-0' } });
      void r.body?.cancel().catch(() => {});
      return r.ok;
    } catch {
      return false;
    }
  })().then((ok) => { corsChecked.set(url, ok); corsChecking.delete(url); return ok; });
  corsChecking.set(url, p);
  return p;
}

/** probeCors's answer if it has one already. */
export function corsKnown(url: string): boolean | undefined {
  return corsChecked.get(url);
}
